import { NavigationVariant } from '../Navigation';

export interface SitecoreImageFieldValue {
    [attributeName: string]: unknown;
    src?: string;
}

export interface SitecoreImageField {
    value?: SitecoreImageFieldValue;
    editable?: string;
}

export const ABTest = {
    DRBanner: 'DRBannerAbTest',
};

export interface AbTestJson {
    variant: string;
    expirationDate: Date;
}

export enum AccordionColumns {
    ONE = 'ONE',
    TWO = 'TWO',
}

export interface Article {
    title: string;
    date: string;
    url: string;
}

export interface BlogListProps {
    items: Array<Article>;
}

export enum ButtonType {
    Contained = 'Contained',
    Text = 'Text',
}

export interface DashboardImage {
    backgroundImage: {
        alt: string;
        src: string;
        height: number;
        width: number;
    };
}

export interface DashboardImageResponse {
    item: DashboardImage;
}

export interface Faq {
    title: {
        value: string;
    };
    description: {
        value: string;
    };
}

export interface FooterNavigationResponse {
    item: {
        featuredItems: {
            value: string;
        };
        unfeaturedItems: {
            value: string;
        };
    };
}

export interface FrequentlyAskedQuestions {
    title: {
        value: string;
    };
    description: {
        value: string;
    };
    accordionItem: {
        targetItems: Faq[];
    };
    columns: {
        value: AccordionColumns;
    };
}

export interface FrequentlyAskedQuestionsResponse {
    item: FrequentlyAskedQuestions;
}

export interface HeaderNavigationResponse {
    item: {
        mainItems: {
            value: string;
        };
        hamburgerItems: {
            value: string;
        };
    };
}

export interface HomeFooterData {
    item: {
        featuredItems: {
            value: string;
        };
        unfeaturedItems: {
            value: string;
        };
    };
}

export interface HomeHeaderData {
    item: {
        mainItems: {
            value: string;
        };
        hamburgerItems: {
            value: string;
        };
        featuredItems: {
            value: string;
        };
        unfeaturedItems: {
            value: string;
        };
    };
}

export interface HomeHeadlineTitles {
    title: string;
    subtitle: string;
}

export interface HomeHeadlineTitlesProps {
    item: {
        title: { value: string };
        subtitle: { value: string };
    };
}

export interface HomeLogo {
    item: {
        Logo: {
            src: string;
            alt: string;
            width?: number;
            height?: number;
        };
        LogoMobile: {
            src: string;
            alt: string;
            width?: number;
            height?: number;
        };
    };
}

export interface HowItWorksBanner {
    title: {
        value: string;
    };
    subtitle: {
        value: string;
    };
    description: {
        value: string;
    };
    message: {
        value: string;
    };
    image: {
        alt: string;
        src: string;
        height: number;
        width: number;
    };
    button: {
        text: string;
        url: string;
        target: string;
    };
}

export interface HowItWorksBannerResponse {
    item: HowItWorksBanner;
}

export interface JumbotronListResponse {
    results: JumbotronResponse[];
}

export interface JumbotronResponse {
    name: string;
    title: {
        value: string;
        jsonValue: {
            value: string;
        };
    };
    text: {
        value: string;
        jsonValue: {
            value: string;
        };
    };
    firstButton: {
        text: string;
        url: string;
        target: string;
    };
    secondButton: {
        text: string;
        url: string;
        target: string;
    };
    firstNote: {
        value: string;
        jsonValue: {
            value: string;
        };
    };
    secondNote: {
        value: string;
        jsonValue: {
            value: string;
        };
    };
    contentAlignment: {
        value: string;
    };
}

export interface MenuNavigationResponse {
    displayName: string;
    url: {
        path: string;
    };
}

export interface MenuResponse {
    title: {
        value: string;
    };
    subTitle: {
        value: string;
    };
    description: {
        value: string;
    };
    link: {
        url: string;
        target: string;
        text: string;
    };
    isNew: {
        boolValue: boolean;
    };
    image: {
        jsonValue: {
            value: {
                src: string;
                alt: string;
                width: string;
                height: string;
            };
        };
    };
    showCameraIcon: {
        boolValue: boolean;
    };
}

export interface MenuologyResponse {
    item: {
        value: string;
    };
    itemDescription: {
        value: string;
    };
    price: {
        value: string;
    };
}

type Variants = Record<NavigationVariant, { key: string; value: NavigationVariant }>;
export const NavigationVariants: Variants = {
    main: {
        key: 'Main Items',
        value: NavigationVariant.Main,
    },
    hamburger: {
        key: 'Hamburger Items',
        value: NavigationVariant.Hamburger,
    },
    featured: {
        key: 'Featured Items',
        value: NavigationVariant.Featured,
    },
    unfeatured: {
        key: 'Unfeatured Items',
        value: NavigationVariant.Unfeatured,
    },
};

export type Products = 'Outback' | 'Carrabbas' | 'Bonefish';

export interface RedirectResponse {
    item: {
        id: string;
        name: string | null;
        children: {
            results: RedirectItem[];
        };
    };
}

export type SitecoreField = {
    name: string | null;
    value: string | null;
} | null;

export type RedirectItem = {
    name: string | null;
    fields: SitecoreField[] | null;
} | null;

export interface SitecoreChildrenResponse<T> {
    item: {
        children: T;
    };
}

export interface SitecoreHomeData {
    item: {
        fields: Array<{ name: string; value: string }>;
    };
}

export type SitecoreRawItems<T> = {
    items: T[];
};

export type SitecoreRawItemsResults<T> = {
    items: {
        results: T[];
    };
};

export interface SitecoreRawChildren {
    results: {
        url: {
            path: string;
        };
        displayName: string;
        id: string;
        openInANewTab: {
            boolValue: boolean;
        };
        link?: {
            jsonValue: { value: { href: string } };
        };
        children: SitecoreRawChildren;
        fields: {
            name: string;
            value: string;
        }[];
    }[];
}

export interface SitecoreResultsResponse<T> {
    item: {
        children: {
            results: T;
        };
    };
}

export interface SitecoreRoute {
    id: string;
    url: string;
    label: string;
    newTab: boolean;
}

export interface SitecoreRouteItem {
    item: {
        id: string;
        displayName: string;
        link: {
            jsonValue: { value: { target: string; href: string } };
        };
        url: {
            path: string;
        };
        label: string;
        newTab: boolean;
    };
}

export interface SitecoreVariantRenderingParamenterResponse {
    params: {
        Variant: string;
    };
}

export interface SitemapItemResponse {
    name: string;
    path: string;
    noIndex: {
        boolValue: boolean;
    };
}

export interface SitemapResponse {
    sitemap: {
        results: SitemapItemResponse[];
    };
}

export interface TabNavigationChildrenResponse {
    TabNavigationItens: SitecoreResultsResponse<TabNavigationResponse>[];
}

export interface TabNavigationResponse {
    displayName: string;
    url: {
        path: string;
    };
}
